import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { CloseButton, Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { SelectedAttachmentContext } from "../../contexts/SelectedAttachmentContext";
import { ContentGroupContext } from "../../providers/ContentGroupProvider";
import { checkImagesSize, workspaceSizeAlert } from "../../utils";
import { PopupMessage as DeleteAttachmentsPopup } from "../popup";
import { alertActions, contentActions } from "../../actions";
import { CONTENT_GROUP_ACTION, sessionConstants, userConstants } from "../../constants";
import axios from "axios";
import { use } from "react";
import { createCancelToken } from "../../utils/Axios";

const SelectedAttachmentPanel = ({ attachments }) => {
    // Contexts
    const { selected_attachments, setSelectedAttachments } = useContext(SelectedAttachmentContext);
    const {
        handleContentInlineEdit,
        workspace_id,
        content_group_id,
        readOnly
    } = useContext(ContentGroupContext);

    // Redux
    const {
        currentWorkSpace,
        can_access_content_destroy,
        can_access_content_update,
        can_access_content_download,
        attachment_downloading,
        content_loading
    } = useSelector(state => state.content);

    const dispatch = useDispatch();

    // Refs & State
    const hiddenFileInput = useRef(null);
    const [deletePopup, setDeletePopup] = useState(false);
    const cancelTokenRef = useRef(createCancelToken());

    // Derived values
    const deletedSelectedAttachmentText = useMemo(() => {
        const count = selected_attachments.length;
        return `${count} card${count > 1 ? "s" : ""}`;
    }, [selected_attachments]);

    const deletePopupMessage = `Are you sure you want to delete ${deletedSelectedAttachmentText}?`;

    // Handlers
    const handleFileUploadClick = () => {
        hiddenFileInput.current.click();
    };

    const handleFileChange = e => {
        if (!can_access_content_update || readOnly) return;
        if (selected_attachments.length !== 1) return;

        const selectedAttachmentId = selected_attachments[0].id;
        const file = e.target.files[0];

        if (!checkImagesSize([file], currentWorkSpace?.personal) && file) {
            workspaceSizeAlert(currentWorkSpace);
            handleContentInlineEdit({ attachment: file }, selectedAttachmentId);
        }

        e.target.value = "";
        setSelectedAttachments([]);
    };

    const handleClose = () => setSelectedAttachments([]);

    const handleSelectAll = () => setSelectedAttachments(attachments);

    const downloadFiles = files => {
        if(attachment_downloading) return;
        if (!can_access_content_download) return;

        dispatch({ type: CONTENT_GROUP_ACTION.DOWNLOADING_ATTACHMENT });

        const fileIds = files.map(file => file.id);

        const onSuccess = response => {
            const blob = response.data;
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            const disposition = response.headers["content-disposition"];
            const match = disposition?.match(/filename="?([^"]+)"?/);
            const filename = match?.[1] || "downloaded_file";
            a.href = url;
            a.download = filename;
            a.click();
            a.remove();
        };

        const onError = error => {
            if(error?.response?.status === 429) {
                dispatch(alertActions.error(sessionConstants.SERVER_LIMIT_EXCEEDED));
            }
        };

        dispatch(contentActions.downloadContents({
            workspace_id,
            content_group_id,
            ids: fileIds,
            cancel_token_source: cancelTokenRef.current,
            onSuccess,
            onError,
            onFinally: () => {
                dispatch({ type: CONTENT_GROUP_ACTION.DOWNLOADED_ATTACHMENT });
                handleClose();
            }
        }));
    };

    const handleDownload = () => {
        const selectedIds = new Set(selected_attachments.map(item => item.id));
        const selectedFiles = attachments.filter(item => selectedIds.has(item.id));
        if (selectedFiles.length === 0) return;

        downloadFiles(selectedFiles);
    };

    const handleDelete = () => {
        if (!can_access_content_destroy || readOnly) return;

        const selectedIds = selected_attachments.map(item => item.id);

        dispatch(contentActions.destroyContents({
            workspace_id,
            content_group_id,
            ids: selectedIds,
            onBefore: () => {
                dispatch({ type: userConstants.CONTENT_LOADING });
                setDeletePopup(false);
            },
            onSuccess: response => {
                const deleted_content_ids = response.data.data.contents.map(item => item.id);
                dispatch({ type: userConstants.DELETE_CONTENTS, payload: deleted_content_ids });
                dispatch(alertActions.success(userConstants.DELETE_CONTENT));
            },
            onError: response => dispatch(alertActions.error(response.data.message)),
            onFinally: () => {
                handleClose();
                setDeletePopup(false);
                dispatch({ type: userConstants.CONTENT_LOADING_STOP });
            }
        }));
    };

    useEffect(() => {
        return () => {
            if (cancelTokenRef.current) {
                cancelTokenRef.current.cancel("Operation canceled by the user.");
                cancelTokenRef.current = createCancelToken();
            }
        };
    }, []);

    const downloadDisabled = useMemo(() => {
        return attachment_downloading || content_loading || selected_attachments.length === 0;
    }, [attachment_downloading, content_loading, selected_attachments.length]);

    // Early return if nothing is selected
    if (!selected_attachments || selected_attachments.length === 0) return null;

    return (
        <div className="selected-attachment-panel p-1 d-xl-flex justify-content-between align-items-center">
            <input
                type="file"
                onChange={handleFileChange}
                ref={hiddenFileInput}
                hidden
            />

            <DeleteAttachmentsPopup
                show={deletePopup}
                onClose={() => setDeletePopup(false)}
                message={deletePopupMessage}
                onConfirm={handleDelete}
            />

            <div className="d-flex align-items-center">
                <CloseButton className="btn-sm btn btn-outline-gray me-2 p-0" onClick={handleClose} />
                <Button
                    className="px-3"
                    size="sm"
                    variant="light"
                    onClick={handleSelectAll}
                >
                    Select All
                </Button>
            </div>

            <div>
                {selected_attachments.length === 1 && can_access_content_update && !readOnly && (
                    <Button
                        className="px-3 mx-1"
                        size="sm"
                        variant="light"
                        onClick={handleFileUploadClick}
                        disabled={downloadDisabled}
                    >
                        Replace
                    </Button>
                )}
                {can_access_content_download && (
                    <Button
                        className="px-3 mx-1"
                        size="sm"
                        variant="light"
                        onClick={handleDownload}
                        disabled={downloadDisabled}
                    >
                        Download
                    </Button>
                )}
                {can_access_content_destroy && selected_attachments.length > 0 && !readOnly && (
                    <Button
                        className="px-3 mx-1"
                        size="sm"
                        variant="light"
                        onClick={() => setDeletePopup(true)}
                        disabled={downloadDisabled}
                    >
                        Delete
                    </Button>
                )}
            </div>
        </div>
    );
};

export { SelectedAttachmentPanel };
